import React, { useState, MouseEvent } from 'react';
import { motion } from 'framer-motion';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const VariableContainer = styled(Box)({
  width: '100%',
  textAlign: 'left',
  borderRadius: '5px',

  '&:hover': {
    outline: '2px solid #0289d1',
    backgroundColor: '#e5f6fd',
  },
});

interface VariableProps {
  onClick: () => void;
  label: string;
}

const Variable = ({ label, onClick }: VariableProps) => {
  return (
    <VariableContainer onClick={onClick}>
      <Typography variant="body2" sx={{ p: 2 }}>
        {label}
      </Typography>
    </VariableContainer>
  );
};

interface VariablesButtonProps {
  variables: Record<string, { id: string; display: string }[]>;
  handleConcatInputValue: (concatVal: string) => void;
}

const VariablesButton = ({
  variables,
  handleConcatInputValue,
}: VariablesButtonProps) => {
  const [variablesBtnAnchorEl, setVariablesBtnAnchorEl] =
    useState<HTMLDivElement | null>(null);

  return (
    <>
      <motion.div
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.05 }}
        onClick={(e) => setVariablesBtnAnchorEl(e.currentTarget)}
      >
        <Box id="variables-picker" sx={{ marginRight: '10px' }}>
          <Tooltip title="Variables picker" placement="top" enterDelay={1000}>
            <span>
              <Button
                disableRipple
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
              >
                Personalization
              </Button>
            </span>
          </Tooltip>
        </Box>
      </motion.div>

      <Popover
        open={Boolean(variablesBtnAnchorEl)}
        anchorEl={variablesBtnAnchorEl}
        onClose={() => setVariablesBtnAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List
          sx={{
            p: 2,
            maxHeight: '420px',
          }}
        >
          {Boolean(variables) &&
            Object.keys(variables).map((key) => {
              const variablesData = variables[key];
              return (
                <>
                  <ListSubheader sx={{ p: 0, mb: 1, mt: 1 }}>
                    <Typography variant="h6">
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Typography>
                  </ListSubheader>
                  {variablesData.map((variableSlug) => (
                    <Variable
                      key={variableSlug.id}
                      label={variableSlug.display}
                      onClick={() => {
                        handleConcatInputValue(`{{${variableSlug.display}}}`);
                        setVariablesBtnAnchorEl(null);
                      }}
                    />
                  ))}
                </>
              );
            })}
        </List>
      </Popover>
    </>
  );
};

export default VariablesButton;
